import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SparklesIcon, PlayIcon, StopIcon, MicrophoneIcon, CloudArrowUpIcon, PhotoIcon, XMarkIcon, TrashIcon, ArrowsRightLeftIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';
import type { ScriptCharacter } from '@packages/global';
import { AssetType, AssetGenerationStatus, IProjectAsset } from '@packages/global';
import AudioUpload from '../../common/AudioUpload';
import MediaPreviewModal from '../../common/MediaPreviewModal';
import { projectAssetApi } from '../../../services/user/projectAsset.api';
import ConfirmDialog from '../../common/ConfirmDialog';
import AssetGenerateModalV2 from '../../projectAssets/AssetGenerateModalV2';
import ProjectAssetSelector from '../../projectAssets/ProjectAssetSelector';

// 检查是否为认证角色
const isVerifiedCharacter = (asset?: IProjectAsset): boolean => {
  if (!asset) return false;
  if (asset.assetType !== AssetType.CHARACTER) return false;
  if (!asset.assetId) return false;
  if (!asset.conceptImageUrl) return false;

  // 检查 conceptImageUrl 中是否包含 assetId 参数
  try {
    const url = new URL(asset.conceptImageUrl, window.location.origin);
    const assetIdParam = url.searchParams.get('assetId');
    return !!assetIdParam;
  } catch {
    return false;
  }
};

interface CharacterCardProps {
  character: ScriptCharacter;
  playingAudio: string | null;
  onPlayAudio: (url: string, id: string) => void;
  scriptId: string;
  projectId?: string;
  episodeId?: string;
  artStyle?: string;  // 新增：艺术风格
  onUpdate?: () => void;
  /** 图片正在生成中，禁用所有操作 */
  imageGenerating?: boolean;
  /** 来自 ProjectAsset 的生成状态（优先级高于 imageGenerating） */
  assetGenerationStatus?: AssetGenerationStatus;
  /** 来自 ProjectAsset 的图片 URL（优先级高于 character.imageUrl） */
  assetImageUrl?: string;
  /** 关联的 ProjectAsset 对象（用于认证检查） */
  matchedAsset?: IProjectAsset;
}

export default function CharacterCard({ character, playingAudio, onPlayAudio, scriptId, projectId, episodeId, artStyle, onUpdate, imageGenerating, assetGenerationStatus, assetImageUrl, matchedAsset }: CharacterCardProps) {
  const { t } = useTranslation(['script', 'common']);
  const [showAudioUpload, setShowAudioUpload] = useState(false);
  const [showImageGenDialog, setShowImageGenDialog] = useState(false);
  const [showImageGenMode, setShowImageGenMode] = useState<'text' | 'image'>('text');
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showReplaceSelector, setShowReplaceSelector] = useState(false);
  const isPlaying = playingAudio === `character-${character.name}`;

  // 优先使用 ProjectAsset 的图片 URL，其次使用脚本 JSON 字段
  const displayImageUrl = assetImageUrl || character.imageUrl;

  // 单个 asset 的生成状态（来自 ProjectAsset）
  const isAssetGenerating =
    assetGenerationStatus === AssetGenerationStatus.PENDING ||
    assetGenerationStatus === AssetGenerationStatus.GENERATING;
  // 卡片 loading 只反映"自身"是否在生成，不因整个步骤 imageGenerating 而覆盖已完成的卡片
  const isLoadingImage = isAssetGenerating || updating;
  // 交互禁用：当前 asset 正在生成，或整个步骤在生成（避免用户重复点击触发批量生成中的 asset）
  const isInteractionDisabled = isLoadingImage || imageGenerating;

  const isVerified = isVerifiedCharacter(matchedAsset);

  const handleVoiceSelect = async (audioUrl: string) => {
    if (!audioUrl) {
      return;
    }
    if (!matchedAsset?.id) {
      alert(t('characters.updateVoiceFailed'));
      return;
    }

    try {
      setUpdating(true);
      await projectAssetApi.updateProjectAsset(matchedAsset.id, { voiceReference: audioUrl });
      onUpdate?.();
      setShowAudioUpload(false);
    } catch (error: any) {
      console.error('更新角色音色失败:', error);
      alert(error.message || t('characters.updateVoiceFailed'));
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!matchedAsset?.id) {
      alert(t('characters.deleteFailed'));
      return;
    }
    try {
      setUpdating(true);
      await projectAssetApi.deleteProjectAsset(matchedAsset.id);
      onUpdate?.();
      setShowDeleteConfirm(false);
    } catch (error: any) {
      console.error('删除角色失败:', error);
      alert(error.message || t('characters.deleteFailed'));
    } finally {
      setUpdating(false);
    }
  };

  const handleReplaceFromAsset = async (asset: IProjectAsset, value: string) => {
    if (!matchedAsset?.id) {
      alert(t('characters.replaceFailed'));
      return;
    }
    setUpdating(true);
    try {
      await projectAssetApi.updateProjectAsset(matchedAsset.id, {
        description: asset.description,
        prompt: asset.prompt || '',
        conceptImageUrl: asset.conceptImageUrl || value,
        voiceReference: asset.voiceReference,
      });
      onUpdate?.();
      setShowReplaceSelector(false);
    } catch (error: any) {
      alert(error.message || t('characters.replaceFailed'));
    } finally {
      setUpdating(false);
    }
  };

  const handleImageGeneration = async (result: { imageUrl: string; prompt: string; subjectDescription?: string }) => {
    if (!matchedAsset?.id) {
      alert(t('characters.updateImageFailed'));
      return;
    }
    setUpdating(true);
    try {
      await projectAssetApi.updateProjectAsset(matchedAsset.id, {
        conceptImageUrl: result.imageUrl,
        prompt: result.prompt,
        // 同步保存弹窗中编辑后的描述，避免描述与图片/提示词不一致导致生成时使用旧描述
        ...(result.subjectDescription !== undefined ? { description: result.subjectDescription } : {}),
      });
      onUpdate?.();
    } catch (error: any) {
      console.error('更新角色图片失败:', error);
      alert(error.message || t('characters.updateImageFailed'));
    } finally {
      setUpdating(false);
    }
  };

  return (
    <>
      <div className="bg-bg-surface rounded-lg overflow-hidden group">
        <div className="relative w-full aspect-square">
          {isLoadingImage ? (
            <div className="w-full h-full bg-bg-elevated flex flex-col items-center justify-center gap-2">
              <div className="relative">
                <SparklesIcon className="w-8 h-8 text-blue-400 animate-pulse" />
                <div className="absolute inset-0 bg-blue-400/20 rounded-full animate-ping" />
              </div>
              {isAssetGenerating && !updating && (
                <span className="text-xs text-blue-300">{t('characters.imageGenerating')}</span>
              )}
            </div>
          ) : displayImageUrl ? (
            <>
              {imageLoading && (
                <div className="absolute inset-0 bg-bg-elevated flex items-center justify-center">
                  <div className="relative">
                    <SparklesIcon className="w-8 h-8 text-blue-400 animate-pulse" />
                    <div className="absolute inset-0 bg-blue-400/20 rounded-full animate-ping" />
                  </div>
                </div>
              )}
              <img
                src={displayImageUrl}
                alt={character.name}
                className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                onLoad={() => setImageLoading(false)}
                onError={() => setImageLoading(false)}
                onClick={() => setShowImagePreview(true)}
              />
            </>
          ) : (
            <div className="w-full h-full bg-bg-elevated flex items-center justify-center">
              <PhotoIcon className="w-10 h-10 text-gray-500" />
            </div>
          )}
          {/* 认证角标 */}
          {isVerified && displayImageUrl && (
            <div
              className="absolute top-2 left-2 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full p-1 shadow-lg ring-2 ring-yellow-300/50"
              title={t('projectSettings:asset.verified')}
            >
              <CheckBadgeIcon className="h-3.5 w-3.5 text-white drop-shadow-md" />
            </div>
          )}
          <button
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isInteractionDisabled}
            className="absolute top-2 right-2 p-1.5 bg-red-600/80 hover:bg-red-700 backdrop-blur-sm rounded-full text-white transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-50 disabled:cursor-not-allowed"
            title={t('characters.deleteCharacter')}
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>

        <div className="p-3 space-y-2">
          <div className="text-sm font-semibold text-gray-50">{character.name}</div>
          <div className="text-xs text-gray-400 truncate">{character.description || character.name}</div>
          {/* 操作按钮 - 匹配设计图样式 */}
          <div className="flex items-center gap-2">
            {character.audioUrl && (
              <button
                onClick={() => onPlayAudio(character.audioUrl!, `character-${character.name}`)}
                disabled={isInteractionDisabled}
                className={`p-1.5 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isPlaying ? 'bg-bg-elevated hover:bg-bg-surface' : 'bg-bg-elevated hover:bg-bg-surface'}`}
                title={isPlaying ? t('characters.stopPlay') : t('characters.playVoice')}
              >
                {isPlaying ? <StopIcon className="w-3.5 h-3.5 text-gray-300" /> : <PlayIcon className="w-3.5 h-3.5 text-gray-300" />}
              </button>
            )}
            <button
              onClick={() => setShowAudioUpload(true)}
              disabled={isInteractionDisabled}
              className="p-1.5 bg-bg-elevated hover:bg-bg-surface rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={t('characters.selectVoice')}
            >
              <MicrophoneIcon className="w-3.5 h-3.5 text-gray-300" />
            </button>
            <button
              onClick={() => { setShowImageGenMode('text'); setShowImageGenDialog(true); }}
              disabled={isInteractionDisabled}
              className="p-1.5 bg-bg-elevated hover:bg-bg-surface rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={t('characters.aiGenerateImage')}
            >
              <SparklesIcon className="w-3.5 h-3.5 text-primary" />
            </button>
            <button
              onClick={() => { setShowImageGenMode('image'); setShowImageGenDialog(true); }}
              disabled={isInteractionDisabled}
              className="p-1.5 bg-bg-elevated hover:bg-bg-surface rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={t('characters.uploadRefImage')}
            >
              <CloudArrowUpIcon className="w-3.5 h-3.5 text-gray-300" />
            </button>
            <button
              onClick={() => setShowReplaceSelector(true)}
              disabled={isInteractionDisabled}
              className="p-1.5 bg-bg-elevated hover:bg-bg-surface rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              title={t('characters.replaceFromLibrary')}
            >
              <ArrowsRightLeftIcon className="w-3.5 h-3.5 text-gray-300" />
            </button>
          </div>
        </div>
      </div>

      {/* 全屏模态框 - 音频上传/音色选择 */}
      {showAudioUpload && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-bg-elevated border border-border-subtle rounded-lg shadow-lg p-4 max-w-md w-full">
            <div className="flex justify-between items-center mb-3">
              <span className="text-sm text-gray-300">{t('characters.selectCharacterVoice')}</span>              <button
                onClick={() => setShowAudioUpload(false)}
                className="text-gray-500 hover:text-gray-300"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <AudioUpload
              onUploadSuccess={handleVoiceSelect}
              projectId={projectId}
              showAudioSelector={true}
              showToneSelector={true}
            />
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title={t('characters.deleteCharacter')}
        message={t('characters.deleteConfirm', { name: character.name })}
        confirmText={t('common:delete')}
        cancelText={t('common:cancel')}
        type="danger"
      />

      <AssetGenerateModalV2
        isOpen={showImageGenDialog}
        onClose={() => setShowImageGenDialog(false)}
        subjectType={AssetType.CHARACTER}
        subjectName={character.name}
        subjectDescription={character.description}
        initialPrompt={character.prompt}
        artStyle={artStyle}
        existingImageUrl={displayImageUrl}
        initialMode={showImageGenMode}
        onConfirm={handleImageGeneration}
      />

      <MediaPreviewModal
        isOpen={showImagePreview}
        onClose={() => setShowImagePreview(false)}
        type="image"
        src={displayImageUrl || ''}
        alt={character.name}
      />

      <ProjectAssetSelector
        isOpen={showReplaceSelector}
        projectId={projectId}
        onSelect={handleReplaceFromAsset}
        onClose={() => setShowReplaceSelector(false)}
        initialAspect="image"
        initialAssetType={AssetType.CHARACTER}
      />
    </>
  );
}
